import { useEffect, useState } from "react"
import { useFindAcquisitionModes } from "../../api/AcquisitionModeApi"
import { useFetchProjectFullData, useUpdateProject } from "../../api/ProjectsApi"
import { Step, StepperForm } from "../../components/StepperForm/StepperForm"
import { ProjectTitle } from "../../components/Titles/ProjectTitle"
import { useNexusNavigate } from "../../hooks/useNexusNavigate"
import { useNexusProjectNumber } from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { ConfirmationRoutes } from "../../routing/NexRoutes"
import { useProjectEditStore } from "../../zstore/ProjectEditStore"
import { AOITab } from "./AOITab"
import { ProjectTab } from "./ProjectTab"
import { getProjectValidatorModel } from "./ProjectValidationModel"

const steps: Step[] = [
    { label: "Project Details", key: "projectTab" },
    { label: "AOIs", key: "aoiTab" },
]

const getStepContent = (activeStep: number) => {
    switch (activeStep) {
        case 0:
            return <ProjectTab />
        case 1:
            return <AOITab />
        default:
            return "Error loading content"
    }
}

export const ProjectEditor = () => {
    //Simple Hooks
    useTitle("Project Editor")
    const projectNumber = useNexusProjectNumber()
    const nexusNavigate = useNexusNavigate()

    // State Management Hooks
    const [activeStep, setActiveStep] = useState(0)
    const projectEdited = useProjectEditStore((state) => state.projectEdited)
    const setProjectData = useProjectEditStore((state) => state.setProjectData)
    const resetProjectEditStore = useProjectEditStore((state) => state.reset)
    const project = useProjectEditStore((state) => state.project)
    const aoi = useProjectEditStore((state) => state.selectedAoi)
    const aoiEdited = useProjectEditStore((state) => state.aoiEdited)
    const error = useProjectEditStore((state) => state.error)
    const setError = useProjectEditStore((state) => state.setError)
    const deletingAoi = useProjectEditStore((state) => state.deletingAoi)
    const parsingAoi = useProjectEditStore((state) => state.parsingAoi)

    // Tanstack Query Hooks
    const projectResponse = useFetchProjectFullData(projectNumber)
    const acquisitionModesResponse = useFindAcquisitionModes()
    const updateProjectMutation = useUpdateProject()

    // Derived Component State
    const isEdited = projectEdited || aoiEdited
    const requestError =
        projectResponse.error?.message ??
        acquisitionModesResponse.error?.message ??
        updateProjectMutation.error?.message
    const loading = projectResponse.isLoading || acquisitionModesResponse.isLoading

    // Use Effects
    useEffect(() => {
        if (projectResponse.data != null) {
            setProjectData(projectResponse.data)
        } else {
            resetProjectEditStore()
        }

        return resetProjectEditStore
    }, [projectResponse.data?.project?.project_number])

    useEffect(() => {
        if (requestError != null) {
            setError(requestError)
        }
    }, [requestError])

    // Handler Functions

    /**
     * Navigate to confirmation page after saving project.
     */
    const handleConfirmation = () => {
        nexusNavigate.confirmationPage(ConfirmationRoutes.PROJECT_EDITED, {
            projectNumber,
            entityName: projectNumber,
        })
    }

    /**
     * Move back or forward one step. If out of forward steps,
     * attempt to submit project.
     */
    const handleStepChange = async (newStep: number) => {
        if (newStep === steps.length) {
            if (project == null) {
                throw new Error("Cannot submit while project is null.")
            }
            await updateProjectMutation.mutateAsync({ selectedProjectData: project, selectedAOIData: aoi })
            handleConfirmation()
        } else if (newStep === -1) {
            nexusNavigate.goHome()
        } else {
            setActiveStep(newStep)
        }
    }

    return (
        <StepperForm
            errorMessages={[error]}
            onErrorClose={() => setError(undefined)}
            steps={steps}
            activeStep={activeStep}
            submitting={updateProjectMutation.isPending}
            disableSubmit={deletingAoi || parsingAoi || !isEdited}
            submitTooltip={!isEdited ? "Edit project or AOI to submit" : ""}
            onStepChange={handleStepChange}
            validationProps={{
                dependencies: [project, aoi, loading],
                model: getProjectValidatorModel(project, aoi),
            }}
            header={<ProjectTitle projectNumber={projectNumber!} />}
            loading={loading}
        >
            {getStepContent(activeStep)}
        </StepperForm>
    )
}
